import { z } from "zod";

/**
 * Environment configuration schema
 *
 * ZenCube OAuth Configuration:
 * - VITE_ZENCUBE_CLIENT_ID: OAuth client ID from ZenCube (required - must be set in environment)
 * - VITE_ZENCUBE_REDIRECT_URI: Callback URL after ZenCube authorization
 * - VITE_ZENCUBE_AUTH_URL: ZenCube OAuth2 base URL (authorization endpoint: {url}/oauth/authorize)
 *
 * Backend configuration (see talent-registry-backend application.yml):
 * - issuer: https://dev-backend.zencube.ai
 *
 * IMPORTANT: OAuth credentials must NOT be hardcoded in source. Set VITE_ZENCUBE_CLIENT_ID in .env
 */
const envSchema = z.object({
  // API Configuration
  VITE_API_URL: z.string().url().default("http://localhost:8080/api/v1"),

  // WebSocket endpoint for real-time chat (STOMP over native WebSocket)
  VITE_WS_URL: z.string().url().default("ws://localhost:8090/ws"),

  // ZenCube OAuth Configuration
  // Client ID is required - must be set via environment variable, no default allowed
  VITE_ZENCUBE_CLIENT_ID: z.string().min(1, "VITE_ZENCUBE_CLIENT_ID is required"),
  VITE_ZENCUBE_REDIRECT_URI: z.string().url().default("http://localhost:5273/auth/callback"),

  // The two ZenCube hosts below are REQUIRED — deliberately no default.
  //
  // They previously defaulted to the dev environment, which is the dangerous kind of default:
  // a plausible, working URL. A missing value did not fail, it silently pointed the app at dev.
  // That is exactly how production's "Go to ZenCube" button ended up opening dev.zencube.ai.
  // (Contrast VITE_API_URL / VITE_WS_URL, whose localhost defaults are safe precisely because
  // they fail visibly if they ever reach a deployed environment.)
  //
  // These are two DIFFERENT hosts — conflating them is the other half of the same bug:
  //   VITE_ZENCUBE_AUTH_URL → ZenCube backend / IdP  (e.g. https://zenv-backend.zencube.ai)
  //   VITE_ZENCUBE_URL      → ZenCube user-facing SPA (e.g. https://zenv.zencube.ai)
  VITE_ZENCUBE_AUTH_URL: z.string().url("VITE_ZENCUBE_AUTH_URL is required (ZenCube backend/IdP)"),
  VITE_ZENCUBE_URL: z.string().url("VITE_ZENCUBE_URL is required (ZenCube user-facing SPA)"),

  // Feature Flags
  VITE_ENABLE_OAUTH: z
    .string()
    .transform((v) => v === "true")
    .default("true"),
  VITE_ENABLE_DEVTOOLS: z
    .string()
    .transform((v) => v === "true")
    .default("true"),

  // Build Mode
  MODE: z.enum(["development", "production", "test"]).default("development"),
});

/**
 * Runtime config injected by the container at startup (docker/entrypoint.sh writes
 * /config.js → `window.__ENV`). This lets a single build-once image be configured per
 * environment at deploy time. In local dev (`pnpm dev`) `window.__ENV` is the empty stub
 * in public/config.js, so values fall back to Vite's build-time `import.meta.env` (.env).
 */
type RuntimeEnv = Partial<Record<string, string>>;
const runtimeEnv: RuntimeEnv =
  (typeof window !== "undefined" && (window as unknown as { __ENV?: RuntimeEnv }).__ENV) || {};

/** Runtime value wins; blank (unset envsubst) and undefined fall back to the build-time value. */
const pick = (runtime: string | undefined, build: unknown): string | undefined => {
  const value = runtime ?? (build as string | undefined);
  return value === undefined || value === "" ? undefined : value;
};

const parseEnv = () => {
  const parsed = envSchema.safeParse({
    VITE_API_URL: pick(runtimeEnv.VITE_API_URL, import.meta.env.VITE_API_URL),
    VITE_WS_URL: pick(runtimeEnv.VITE_WS_URL, import.meta.env.VITE_WS_URL),
    VITE_ZENCUBE_CLIENT_ID: pick(
      runtimeEnv.VITE_ZENCUBE_CLIENT_ID,
      import.meta.env.VITE_ZENCUBE_CLIENT_ID
    ),
    VITE_ZENCUBE_REDIRECT_URI: pick(
      runtimeEnv.VITE_ZENCUBE_REDIRECT_URI,
      import.meta.env.VITE_ZENCUBE_REDIRECT_URI
    ),
    VITE_ZENCUBE_AUTH_URL: pick(
      runtimeEnv.VITE_ZENCUBE_AUTH_URL,
      import.meta.env.VITE_ZENCUBE_AUTH_URL
    ),
    VITE_ZENCUBE_URL: pick(runtimeEnv.VITE_ZENCUBE_URL, import.meta.env.VITE_ZENCUBE_URL),
    VITE_ENABLE_OAUTH: pick(runtimeEnv.VITE_ENABLE_OAUTH, import.meta.env.VITE_ENABLE_OAUTH),
    VITE_ENABLE_DEVTOOLS: pick(
      runtimeEnv.VITE_ENABLE_DEVTOOLS,
      import.meta.env.VITE_ENABLE_DEVTOOLS
    ),
    MODE: import.meta.env.MODE,
  });

  if (!parsed.success) {
    console.error("Invalid environment variables:", parsed.error.flatten().fieldErrors);
    throw new Error("Invalid environment configuration");
  }

  return parsed.data;
};

export const env = parseEnv();
export type Env = z.infer<typeof envSchema>;
